import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Panel, SectionTitle, TactLoader } from "./Tactical";
import MondayCountdown from "./MondayCountdown";
import ScheduleBadge from "./ScheduleBadge";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";

export default function TodaysGames({ leagueId }) {
  const { user } = useAuth();
  const { t } = useT();
  const [today, setToday] = useState(null);
  const [cal, setCal] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const [t, c] = await Promise.all([
        api.get(`/leagues/${leagueId}/today`),
        api.get(`/leagues/${leagueId}/calibration`),
      ]);
      setToday(t.data);
      setCal(c.data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30_000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [leagueId]);

  if (err) return <Panel className="font-mono text-[#E11D48] text-sm" testId="today-error">[ {err} ]</Panel>;
  if (today === null || cal === null) return <TactLoader />;

  return (
    <div data-testid="todays-games" className="space-y-6">
      <div className="flex flex-wrap items-start gap-4 justify-between">
        <div>
          <SectionTitle kicker={t("today.kickoff")} testId="today-title">{t("today.title")}</SectionTitle>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] -mt-4">
            {cal.today_count} · {t("today.title")} · {cal.overdue_count} {t("sb.overdue")} · {cal.my_pending_count} {t("mm.awaitingOpponent")}
          </div>
        </div>
        <MondayCountdown />
      </div>

      {today.length === 0 ? (
        <Panel className="font-mono text-[#71717A] text-sm" testId="today-empty">
          {t("today.empty")}
        </Panel>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="today-list">
          {today.map((m) => {
            const youIn = user && (m.home_id === user.id || m.away_id === user.id);
            const opponentId = user ? (m.home_id === user.id ? m.away_id : m.home_id) : null;
            const Inner = (
              <div className={`bg-[#FFFFFF] dark:bg-[#1A1A1E] border p-5 transition-colors ${youIn ? "border-[#7C3AED] hover:shadow-[0_0_25px_rgba(0,229,255,0.15)]" : "border-[#E4E4E7] dark:border-[#2A2A30] hover:border-[#7C3AED]/60"}`} data-testid={`today-match-${m.id}`}>
                <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{t("fx.round")} {m.round}</span>
                    <ScheduleBadge match={m} />
                  </div>
                  {youIn && (
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED] border border-[#7C3AED]/40 px-2 py-0.5">
                      {t("pp.opponent").replace("الخصم", "أنت").replace("Opponent", "You")}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 items-center gap-2 text-center">
                  <div className="text-right">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{t("fx.home")}</div>
                    <div className={`font-heading text-lg uppercase truncate ${m.home_id === user?.id ? "text-[#34C759]" : ""}`}>{m.home?.username || "—"}</div>
                  </div>
                  <div className="font-mono text-xl text-[#71717A]">{t("mm.vs").toUpperCase()}</div>
                  <div className="text-left">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{t("fx.away")}</div>
                    <div className={`font-heading text-lg uppercase truncate ${m.away_id === user?.id ? "text-[#34C759]" : ""}`}>{m.away?.username || "—"}</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                  <span className={`font-mono text-[10px] uppercase tracking-[0.3em] ${m.status === "confirmed" ? "text-[#34C759]" : m.status === "disputed" ? "text-[#E11D48]" : "text-[#7C3AED]"}`}>
                    {t(`fx.${m.status}`)}
                  </span>
                  {youIn && (
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#7C3AED]">{t("pp.h2hRoom")} →</span>
                  )}
                </div>
              </div>
            );
            return youIn && opponentId ? (
              <Link key={m.id} to={`/h2h/${opponentId}`} className="block" data-testid={`today-link-${m.id}`}>
                {Inner}
              </Link>
            ) : (
              <div key={m.id}>{Inner}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}
